import { toast } from 'sonner';
import { LogFunction } from './types';
import { isUserAdmin } from './helpers';
import { transformInitData } from './transform';
import { clearFirestoreData } from './admin';
import { importFirestoreData } from './import';
import { validateImportData } from './validation';
import { 
  setSystemStatus, 
  // setAggregationEnabled // Removed import
} from '../../lib/api';

/**
 * Initializes Firestore with data from init-data.json
 */
export const initializeFirestore = async (logFn?: LogFunction): Promise<void> => {
  // Check admin status before proceeding
  const isAdmin = await isUserAdmin();
  if (!isAdmin) {
    logFn?.('Permission denied: Admin access required');
    throw new Error('Permission denied: Admin access required');
  }
  
  try {
    logFn?.('Starting database initialization');
    
    // Fetch init data from public folder
    logFn?.('Fetching initialization data from public folder');
    const response = await fetch('/init-data.json');
    if (!response.ok) {
      throw new Error(`Failed to fetch initialization data: ${response.status} ${response.statusText}`);
    }
    const initData = await response.json();
    logFn?.('Successfully loaded initialization data');
    
    // Validate the init data before proceeding
    logFn?.('Validating initialization data structure and content');
    const initDataString = JSON.stringify(initData);
    const validationResult = validateImportData(initDataString);
    
    const validationErrorMessages: string[] = [];
    // Log all validation issues
    validationResult.issues.forEach(issue => {
      const prefix = issue.severity === 'error' ? 'ERROR:' : 'WARNING:';
      const message = `${prefix} ${issue.message}`;
      logFn?.(message);
      if (issue.severity === 'error') {
        validationErrorMessages.push(message);
      }
    });
    
    // If there are any critical errors, stop the initialization
    if (!validationResult.valid) {
      const errorCount = validationResult.issues.filter(i => i.severity === 'error').length;
      const warningCount = validationResult.issues.filter(i => i.severity === 'warning').length;
      
      logFn?.(`Validation failed with ${errorCount} errors and ${warningCount} warnings`);
      if (validationErrorMessages.length > 0) {
        console.error('Validation Errors:', validationErrorMessages);
      }
      
      // Show toast with summary and first few errors
      const errorMessages = validationResult.issues
        .filter(i => i.severity === 'error')
        .map(i => i.message)
        .slice(0, 3); // Show first 3 errors
        
      const summaryMessage = errorMessages.length > 0 
        ? `First issues: ${errorMessages.join('; ')}${errorMessages.length < errorCount ? '...' : ''}`
        : '';
        
      toast.error(`Initialization validation failed: ${errorCount} errors found. ${summaryMessage}`);
      throw new Error(`Initialization validation failed: ${errorCount} errors found. See logs for details.`);
    }
    
    // If there are warnings but no errors, inform the user but continue
    const warningCount = validationResult.issues.filter(i => i.severity === 'warning').length;
    if (warningCount > 0) {
      logFn?.(`Validation succeeded with ${warningCount} warnings - proceeding with initialization`);
      toast.warning(`Initialization proceeding with ${warningCount} validation warnings. Check logs for details.`);
    } else {
      logFn?.('Validation completed successfully with no issues');
    }
    
    // Disable Algolia sync (which also controls aggregation triggers)
    logFn?.('Disabling all triggers (Algolia sync & Firestore aggregations) via syncMode');
    try {
      await setSystemStatus({
        data: {
          algoliaStatus: 'disabled'
        }
      } as any);
      logFn?.('Successfully set Algolia syncMode to disabled (disables all triggers)');
    } catch (error) {
      // If the disable operation fails, abort the entire initialization process
      const errorMessage = error instanceof Error ? error.message : String(error);
      logFn?.(`CRITICAL ERROR: Failed to disable triggers via syncMode: ${errorMessage}`);
      toast.error(`Initialization aborted: Failed to disable triggers properly. This prevents potential data corruption.`);
      throw new Error(`Initialization aborted: Failed to safely disable triggers: ${errorMessage}`);
    }
    
    // If we get here, all triggers were successfully disabled
    
    // Transform the init data to match the Firestore schema
    logFn?.('Transforming initial data to match Firestore schema');
    const transformedData = await transformInitData(initData, logFn);
    const transformedDataString = JSON.stringify(transformedData);
    
    // Clear existing data and import - pass false to clearFirestoreData to avoid redundant trigger management
    logFn?.('Clearing existing database data');
    await clearFirestoreData(logFn, false);
    
    // Import transformed data - importFirestoreData handles aggregation and Algolia sync internally
    logFn?.('Importing transformed initial data');
    await importFirestoreData(transformedDataString, false, logFn, true);
    
    // Aggregation, Algolia sync, and trigger management are already handled in importFirestoreData
    // No need to duplicate those operations here
    
    logFn?.('Database initialized successfully with initial data');
    toast.success("Database initialized with initial data");
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('Error initializing Firestore:', error);
    logFn?.(`Error initializing database: ${errorMessage}`);
    
    // Keep triggers disabled after error
    logFn?.('Keeping triggers disabled after error - manual restart required');
    try {
      await setSystemStatus({
        data: {
          algoliaStatus: 'disabled'
        }
      } as any);
      logFn?.('Successfully kept triggers disabled via syncMode after error');
      toast.error('Error: All triggers remain disabled via syncMode after initialization failure. Please manually re-enable when issues are resolved.');
    } catch (restoreError) {
      const restoreErrorMsg = restoreError instanceof Error ? restoreError.message : String(restoreError);
      logFn?.(`Warning: Failed to disable triggers after error: ${restoreErrorMsg}`);
      toast.error(`Critical: Failed to disable triggers. Please check system status.`);
    }
    
    toast.error(`Failed to initialize database: ${errorMessage}`);
    throw new Error(`Failed to initialize database: ${errorMessage}`);
  }
}; 